import React from 'react';
import { Box, Divider, Typography, IconButton } from '@mui/material';
import _ from 'lodash';
import ChatPerson from './ChatPerson';
import RenderChats from './RenderChats';
import { useAppSelector } from '../../../../../redux/hooks';
import { ChannelLoader } from '../../../../../Loader/chat/ChatLoader';
import ChatUnreadNotificationsBox from '../ChatUnreadNotificationsBox';
import AddIcon from '@mui/icons-material/Add';

interface IChatChannelsProps {
  activeChat: string;
  filteredDirectMessages: IThread[];
  threadValue: string;
  formattedAcsOrgUsers: IFormattedAcsOrgUser[];
  commId: string;
  activeItemRef: React.MutableRefObject<any>;
  filterParticipantsFromDirectThreads: IChatThread[];
  groupMessages: IGroupMessage[];
  activeMessageId: any;
  accessUserChatShow?: any;
  loading?: boolean;
  onCreateGroup: () => void;
  onCreateDirect: () => void;
}

const ChatChannelsContainer: React.FC<IChatChannelsProps> = ({
  activeChat: _activeChat,
  filteredDirectMessages,
  threadValue,
  formattedAcsOrgUsers,
  commId,
  activeItemRef,
  filterParticipantsFromDirectThreads,
  groupMessages,
  activeMessageId,
  accessUserChatShow,
  loading,
  onCreateGroup,
  onCreateDirect
}) => {
  const acsChannels = useAppSelector((state) => state.acsChannels);
  const msgCount = { personal: 0, group: 0 };

  const groupThreadsMsgCount = useAppSelector((state) => state?.acsChannels.groupThreads) || [];
  const personalThreadMsgCount: any = useAppSelector((state) => state?.acsChannels.personalThreadsArr) || [];

  groupThreadsMsgCount.forEach((each) => {
    msgCount.group += each.msgCount;
  });
  personalThreadMsgCount.forEach((each: any) => {
    msgCount.personal += each.msgCount;
  });
  /** Combine both arrays */
  /** Calculate total message count */

  /** Set height */
  const boxHeight = accessUserChatShow ? 'calc(100% - 220px)' : 'calc(100% - 135px)';

  const renderGroupChatPersons = () => {
    if (groupMessages.length === 0) {
      return (
        <>
          <Box p={2}>
            <Typography textAlign="center">No chat groups</Typography>
          </Box>
          <Divider />
        </>
      );
    }

    return groupMessages.map((each, idx) => {
      const lastMessageUserName = formattedAcsOrgUsers[each?.senderCommunicationId];
      let message = '';
      if (each?.deleted) {
        message = `This message was deleted`;
      } else if (each.type !== 'text') {
        message = 'Group details updated';
      } else if (each.file) {
        message = 'Sent an attachment';
      } else {
        message = each?.lastmessage || '';
      }
      return (
        <ChatPerson
          key={idx}
          lstMessage={message}
          lstMessageUserName={lastMessageUserName?.displayName}
          type="group-thread"
          activeThreadId={threadValue}
          unReadMessageCount={each?.msgCount}
          activeCommId={commId}
          threadId={_.get(each, 'id', '')}
          name={_.get(each, 'topic') || ''}
          image={_.get(each, 'image', '')}
          lastMessageReceivedOn={_.get(each, 'lastMessageReceivedOn', '')}
          index={groupMessages}
          loading={loading}
          deleted={each.deleted}
          messageType={each.type}
        />
      );
    });
  };

  return (
    <Box width="100%" height={boxHeight} overflow={'hidden auto'} className="chat-v2__sidebar-body">
      {/* Group chat */}
      <Box width="100%" className="chat-v2__section">
        {loading ? (
          <ChannelLoader channelCount={0} />
        ) : (
          <>
            <Box className="chat-v2__section-header">
              <Box className="chat-v2__section-header__title">
                <Typography className="chat-v2__section-title">Groups</Typography>
              </Box>
              <Box className="chat-v2__section-divider-vert" />
              <Box className="chat-v2__section-header__controls">
                {msgCount.group > 0 && (
                  <ChatUnreadNotificationsBox count={msgCount.group} printAres="besideChatHeading" />
                )}
                <IconButton
                  aria-label="create-group"
                  onClick={onCreateGroup}
                  className="chat-v2__plus-btn"
                  size="small"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Divider className="chat-v2__divider" />
            {groupMessages.length === 0 ? (
              <Box className="chat-v2__empty-row">
                <Typography className="chat-v2__empty-text">No chats</Typography>
              </Box>
            ) : (
              <Box>{renderGroupChatPersons()}</Box>
            )}
          </>
        )}
      </Box>

      {/* Person Chat */}
      <Box width="100%" className="chat-v2__section">
        {loading ? (
          <ChannelLoader channelCount={5} />
        ) : (
          <>
            <Box className="chat-v2__section-header">
              <Box className="chat-v2__section-header__title">
                <Typography className="chat-v2__section-title">Direct Messages</Typography>
              </Box>
              <Box className="chat-v2__section-divider-vert" />
              <Box className="chat-v2__section-header__controls">
                {msgCount.personal > 0 && (
                  <ChatUnreadNotificationsBox count={msgCount.personal} printAres="besideChatHeading" />
                )}
                <IconButton
                  aria-label="create-direct-message"
                  onClick={onCreateDirect}
                  className="chat-v2__plus-btn"
                  size="small"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Divider className="chat-v2__divider" />
            <Box data-testid="chat-direct-message-box">
              <RenderChats
                activeItemRef={activeItemRef}
                commId={commId}
                filterParticipantsFromDirectThreads={filterParticipantsFromDirectThreads}
                filteredDirectMessages={filteredDirectMessages}
                formattedAcsOrgUsers={formattedAcsOrgUsers}
                threadValue={threadValue}
                activeMessageId={activeMessageId}
                loading={loading}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ChatChannelsContainer;
